import { useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaEnvelope,
  FaPhone,
  FaUser,
  FaReply,
  FaTrash,
  FaCheckCircle,
  FaStickyNote,
  FaPen,
  FaDotCircle,
  FaEnvelopeOpen,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { initAllStores } from "../../../utils/cache";
import Contact from "../../../models/Contact";
import { timeAgo } from "../../../utils/date";
import { sendRequest } from "../../../utils/api";
import FullPageSpinner from "../../../components/FullPageSpinner";
import { useToast } from "../../../context/ToastContext";
import { useModal } from "../../../context/ConfirmModalContext";


export default function ContactDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToast();
  const { openModal } = useModal();

  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState({} as Contact);

  useEffect(() => {
    const findContact = async () => {

      const cache = await initAllStores();

      const contact = await cache.getBy("contacts", { id }) as Contact;
      setContact(contact);
    }
    findContact();
  }, [id]);


  if (!id) return null;

  const deleteContact = async () => {
    const cache = await initAllStores();

    const data = {
      url: `/contacts/${id}/delete`,
      warningTitle: "Delete Contact Message",
      warningMsg: (
        `Are you sure you want to delete this contact message?` +
        " This action cannot be undone."
      ),
    };

    openModal(
      data.warningTitle,
      data.warningMsg,
      async () => {
        setLoading(true);
        try {
          await sendRequest("DELETE", `/contacts/${id}/delete`);

          await cache.deleteOne("contacts", id);
          showToast("Contact Message Deleted Successfully.", { type: "success" });
        } catch (err: any) {
          console.log(err);
          showToast("An Error Occured. Please try again.", { type: "error" });
        } finally {
          setLoading(false);
          navigate("/contacts");
        }
      },
      true,
      `Yes, Delete`,
      "Cancel",
      "red"
    );
  };


  const updateContact = async (data: Contact) => {
    const cache = await initAllStores();

    setLoading(true);
    try {
      await sendRequest("PUT", `/contacts/${id}/update`, data);
      await cache.updateManyBy("contacts", [id], data);
      showToast("Contact Updated Successfully.", { type: "success" });
    } catch (err: any) {
      console.log(err);
      showToast("An Error Occured. Please try again.", { type: "error" });
    } finally {
      setLoading(false);
    }
  }

  if (!contact) {
    return <div className="p-6">Message not found.</div>;
  }

  const badge = (isRead: boolean) =>
    !isRead
      ? "bg-red-100 text-red-700"
      : "bg-green-100 text-green-700";

  const statusText = (isRead: boolean) =>
    isRead ? "Read" : "Unread";

  return (
    <div className="mx-auto max-w-6xl p-6">

      <FullPageSpinner show={loading} />

      <button
        onClick={() => navigate("/contacts")}
        className="mb-6 flex items-center gap-2 text-gray-600 hover:text-blue-600"
      >
        {FaArrowLeft({})}
        Back
      </button>

      <div className="grid gap-6 lg:grid-cols-3">

        {/* Sender */}

        <div className="rounded-xl border bg-white p-6">

          <div className="flex flex-col items-center">

            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-blue-100">
              <span className="text-4xl text-blue-600" >{FaUser({})}</span>
            </div>

            <h2 className="mt-4 text-2xl font-bold">
              {contact.name}
            </h2>

            <span
              className={`mt-2 rounded-full px-4 py-1 text-sm ${badge(contact.isRead)}`}
            >
              {statusText(contact.isRead)}
            </span>

          </div>

          <div className="mt-8 space-y-5">

            <div className="flex items-center gap-3">
              <span className="text-blue-600" >{FaEnvelope({})}</span>
              <span>{contact.email}</span>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-blue-600">{FaPhone({})}</span>
              <span>{contact.phone}</span>
            </div>

          </div>

        </div>

        {/* Message */}

        <div className="space-y-6 lg:col-span-2">

          <div className="rounded-xl border bg-white p-6">

            <h3 className="mb-4 text-xl font-semibold">
              {contact.subject}
            </h3>

            <div className="mb-6 text-sm text-gray-500">
              Received: {timeAgo(contact.createdAt)}
            </div>

            <div className="rounded-lg bg-gray-50 p-5 leading-8 text-gray-700">
              {contact.message}
            </div>
          </div>

          <div className="rounded-xl border bg-white p-6">

            <h3 className="mb-3 text-lg font-semibold">
              Internal Notes
            </h3>

            <textarea
              value={contact.note ?? ""}
              onChange={(e) =>
                setContact(prev => ({
                  ...prev,
                  note: e.target.value,
                }))}
              rows={5}
              placeholder="Add notes about this conversation..."
              className="w-full rounded-lg border p-4 outline-none focus:border-blue-500"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-white hover:bg-blue-700"
              onClick={() => {
                updateContact(contact);
              }}
            >
              {FaPen({})}
              Save Note
            </button>

            <button
              className={`flex items-center gap-2 rounded-lg ${contact.isRead ? "bg-green-600" : "bg-yellow-600"} px-5 py-3 text-white hover:${contact.isRead ? "bg-green-700" : "bg-yellow-700"}`}
              onClick={() => {
                updateContact({
                  ...contact,
                  isRead: !contact.isRead
                });

                setContact(ct => ({
                  ...ct,
                  isRead: !ct.isRead
                }))
              }}
            >
              {contact.isRead ? FaEnvelope({}) : FaEnvelopeOpen({})}
              {!contact.isRead ? "Mark as Read" : "Mark as Unread"}
            </button>

            <button
              className="flex items-center gap-2 rounded-lg bg-red-600 px-5 py-3 text-white hover:bg-red-700"
              onClick={deleteContact}
            >
              {FaTrash({})}
              Delete
            </button>

          </div>

        </div>

      </div>
    </div>
  );
}